/**
 * The school administrator's own AI panel.
 *
 * This screen exists because whether a school uses AI is the school's decision,
 * not the platform's (DR-029). Before, a school administrator could subscribe,
 * pay, and still see nothing: the switch that actually mattered lived in the
 * site administrator's console, and the only thing this tab could say was "ask
 * someone else".
 *
 * The four decisions are tabs, shown in the support team's working order:
 *
 *   1. **Use AI here at all** - one switch, the school's own.
 *   2. **How it is paid for** - the platform subscription, or the school's own
 *      provider key, with the key entered here because it is the school's key
 *      and the school's bill.
 *   3. **Which features** - only the ones the site offers appear as choices; a
 *      feature the deployment does not have is shown as unavailable rather than
 *      as a switch that silently does nothing. Site-admin controls also live here.
 *   4. **What it has been used for** - this month's calls, so a school can see
 *      what it is paying for without asking.
 */

import { useState } from 'react'
import type { ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type AiSchoolSettings } from '../../lib/api/client'
import { Button, Checkbox, Field, Input, Pill, Select, Spinner, useTabList } from '../../components/ui'
import { toast } from '../../components/toast'

const STATUS_LABELS: Record<string, string> = {
  none: 'No subscription yet',
  created: 'Awaiting payment authorisation',
  authenticated: 'Payment authorisation received',
  active: 'Active monthly subscription',
  trialing: 'Trial subscription',
  pending: 'Payment pending',
  halted: 'Payment retries stopped',
  payment_failed: 'Payment failed',
  canceled: 'Subscription cancelled',
  cancelled: 'Subscription cancelled',
  completed: 'Subscription completed',
  expired: 'Subscription expired',
}

const SUBSCRIBABLE = [
  'none',
  'canceled',
  'cancelled',
  'completed',
  'expired',
  'payment_failed',
]

type AiTab = 'enable' | 'billing' | 'features' | 'usage'

const AI_TABS: [AiTab, string, string][] = [
  ['enable', '1', 'AI features'],
  ['billing', '2', 'How AI is paid'],
  ['features', '3', 'Which features'],
  ['usage', '4', 'AI Usage'],
]

/** The numbered-card shell the platform's AI screens use
 *  (features/platform/schools.css). Reused rather than restyled so a school
 *  administrator and a site administrator are looking at one product; the first
 *  version of this tab used bare `tc-panel` divs, which have no card at all and
 *  left the whole screen as unbounded text running the width of the page. */
function Section({
  title,
  summary,
  children,
}: {
  title: string
  summary?: string
  children: ReactNode
}) {
  return (
    <section className="tc-ai-section">
      <header className="tc-ai-section-head">
        <div>
          {/* h2, not h3: these are the top-level sections of the tab, and the
              page heading above them is the h1. */}
          <h2>{title}</h2>
          {summary && <p className="tc-ai-help">{summary}</p>}
        </div>
      </header>
      <div className="tc-ai-section-body">{children}</div>
    </section>
  )
}

export function SchoolAiSettingsPanel({
  schoolId,
  platformAiControl,
}: {
  schoolId?: string
  platformAiControl?: ReactNode
}) {
  const tabs = useTabList()
  const queryClient = useQueryClient()
  const [requestedTab, setRequestedTab] = useState<AiTab>('enable')
  const school = useQuery({
    queryKey: ['school', schoolId ?? 'current'],
    queryFn: () => api.school(schoolId),
  })
  const resolvedId = school.data?.id
  const settingsKey = ['ai-billing', resolvedId]
  const settings = useQuery({
    queryKey: settingsKey,
    queryFn: () => api.schoolAiSettings(resolvedId as string),
    enabled: Boolean(resolvedId),
  })

  const onError = (error: unknown) =>
    toast(error instanceof Error ? error.message : 'That could not be saved.', 'error')
  // Every mutation writes the server's answer straight back into the cache
  // rather than refetching: these are toggles, and a toggle that flicks back to
  // its old position for half a second reads as a failure.
  const applied = (updated: AiSchoolSettings) => queryClient.setQueryData(settingsKey, updated)

  const setEnabled = useMutation({
    mutationFn: (enabled: boolean) => api.setSchoolAiEnabled(resolvedId as string, enabled),
    onSuccess: (updated) => {
      applied(updated)
      queryClient.invalidateQueries({ queryKey: ['ai', 'status'] })
      toast(updated.ai_enabled_by_school ? 'AI features are on for your school.' : 'AI features are off for your school.')
    },
    onError,
  })
  const setFeature = useMutation({
    mutationFn: (vars: { key: string; enabled: boolean }) =>
      api.setSchoolAiFeature(resolvedId as string, vars.key, vars.enabled),
    onSuccess: (updated, variables) => {
      applied(updated)
      queryClient.invalidateQueries({ queryKey: ['ai', 'status'] })
      const feature = updated.features.find((item) => item.key === variables.key)
      toast(`${feature?.label ?? 'AI feature'} is ${variables.enabled ? 'on' : 'off'} for your school.`)
    },
    onError,
  })

  if (school.isLoading || settings.isLoading) return <Spinner label="Loading AI settings" />
  if (settings.isError) {
    return (
      <div className="tc-ai-suspended" role="alert">
        <p>
          {settings.error instanceof Error
            ? settings.error.message
            : 'AI settings could not be loaded.'}
        </p>
      </div>
    )
  }
  if (!settings.data || !resolvedId) return null
  const data = settings.data

  // Suspension is rare and overrides everything, so it is stated once at the
  // top and the controls below are left disabled rather than hidden - a school
  // administrator should be able to see what their setup is while it is
  // stopped, not be shown an empty screen.
  const suspended = !data.enabled_by_site_admin
  const on = data.ai_enabled_by_school
  const canOpen = (key: AiTab) =>
    key === 'enable' || (key === 'features' && Boolean(platformAiControl)) || (on && !suspended)
  const activeTab = canOpen(requestedTab) ? requestedTab : 'enable'

  return (
    <div className="tc-ai-school">
      {suspended && (
        <div className="tc-ai-suspended" role="alert">
          <strong>AI is suspended for your school.</strong>
          <p className="tc-ai-help">
            TeachCraftAI has paused AI features for this school. Your settings are kept as they
            are. Contact TeachCraftAI to have them restored.
          </p>
        </div>
      )}

      <div className="tc-ai-workflow">
        <div className="tc-ai-workflow-tabs" ref={tabs.ref} onKeyDown={tabs.onKeyDown} role="tablist" aria-label="AI and billing steps">
          {AI_TABS.map(([key, step, label]) => {
            const available = canOpen(key)
            return (
              <button
                key={key}
                id={`tc-school-ai-${key}-tab`}
                type="button"
                role="tab"
                aria-controls={`tc-school-ai-${key}-panel`}
                aria-selected={activeTab === key}
                disabled={!available}
                className={`tc-ai-workflow-tab ${activeTab === key ? 'is-active' : ''}`}
                onClick={() => setRequestedTab(key)}
              >
                <strong aria-hidden="true">{step}</strong>
                <span>{label}</span>
              </button>
            )
          })}
        </div>

        <div
          id={`tc-school-ai-${activeTab}-panel`}
          role="tabpanel"
          aria-labelledby={`tc-school-ai-${activeTab}-tab`}
          className="tc-ai-workflow-panel"
        >
          {activeTab === 'enable' && (
            <Section
              title="AI features"
              summary="Your school decides whether to use AI. Teachers only ever see a suggestion - nothing an AI produces is saved to a paper until a teacher accepts it."
            >
              <div className="tc-ai-master">
                <Checkbox
                  label="Use AI features at this school"
                  checked={on}
                  disabled={suspended || setEnabled.isPending}
                  onChange={(event) => setEnabled.mutate(event.target.checked)}
                />
              </div>
            </Section>
          )}

          {activeTab === 'billing' && on && !suspended && (
            <AccessAndBilling schoolId={resolvedId} data={data} onApplied={applied} />
          )}

          {activeTab === 'features' && (
          <Section
            title="Which features"
            summary="Turn off anything your school would rather teachers did not have."
          >
            {data.features.length === 0 && (
              <p className="tc-ai-help">This site does not offer any AI features yet.</p>
            )}
            {data.features.map((feature) => (
              <div key={feature.key} className="tc-ai-feature-row">
                <div className="tc-ai-feature-row-head">
                  <Checkbox
                    label={feature.label}
                    checked={feature.enabled_for_school}
                    disabled={!on || suspended || !feature.enabled_on_site || setFeature.isPending}
                    onChange={(event) =>
                      setFeature.mutate({ key: feature.key, enabled: event.target.checked })
                    }
                  />
                  {!feature.enabled_on_site && <Pill tone="muted">Not available on this site</Pill>}
                </div>
                <p className="tc-ai-help">{feature.description}</p>
              </div>
            ))}
            {platformAiControl && (
              <div className="tc-ai-platform-slot">
                {platformAiControl}
              </div>
            )}
          </Section>
          )}

          {activeTab === 'usage' && on && !suspended && <Usage schoolId={resolvedId} />}
        </div>
      </div>
    </div>
  )
}

/** How the school pays: the platform subscription, or its own provider key.
 *  Both live in one card because they are the same question answered two ways,
 *  and a school choosing between them needs to see both answers at once. */
function AccessAndBilling({
  schoolId,
  data,
  onApplied,
}: {
  schoolId: string
  data: AiSchoolSettings
  onApplied: (updated: AiSchoolSettings) => void
}) {
  const queryClient = useQueryClient()
  const onError = (error: unknown) =>
    toast(error instanceof Error ? error.message : 'That could not be saved.', 'error')

  const setMode = useMutation({
    mutationFn: (mode: 'subscription' | 'byok') => api.setAiAccessMode(schoolId, mode),
    onSuccess: (updated) => {
      onApplied(updated)
      queryClient.invalidateQueries({ queryKey: ['ai', 'status'] })
    },
    onError,
  })
  const goTo = (url: string) => {
    window.location.href = url
  }
  const checkout = useMutation({
    mutationFn: () => api.startCheckout(schoolId),
    onSuccess: (result) => goTo(result.url),
    onError,
  })
  const portal = useMutation({
    mutationFn: () => api.billingPortal(schoolId),
    onSuccess: (result) => goTo(result.url),
    onError,
  })
  const cancel = useMutation({
    mutationFn: () => api.cancelSubscription(schoolId),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['ai-billing', schoolId] })
      toast(result.message)
    },
    onError,
  })

  const active = data.subscription_status === 'active' || data.subscription_status === 'trialing'
  const canSubscribe = SUBSCRIBABLE.includes(data.subscription_status)
  const canContinueSetup = data.subscription_status === 'created'

  return (
    <Section
      title="How your AI is paid for"
      summary="Either the TeachCraftAI subscription, or a provider key of your own."
    >
      <Field
        label="Access"
        hint="A subscription uses TeachCraftAI's provider and is metered against your monthly allowance. Your own key is billed to you by your provider and is not metered here."
      >
        <Select
          value={data.access_mode}
          disabled={setMode.isPending}
          onChange={(event) => setMode.mutate(event.target.value as 'subscription' | 'byok')}
        >
          <option value="subscription">TeachCraftAI subscription</option>
          <option value="byok">Our own AI provider key</option>
        </Select>
      </Field>

      {data.access_mode === 'subscription' ? (
        <>
          <p>
            <Pill tone={active ? 'success' : 'muted'}>
              {STATUS_LABELS[data.subscription_status] ?? data.subscription_status}
            </Pill>
          </p>
          {data.subscription_current_period_end && active && (
            <p>
              {data.subscription_cancel_at_period_end ? 'Access ends' : 'Renews'}{' '}
              {new Date(data.subscription_current_period_end).toLocaleDateString()}.
            </p>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(canSubscribe || canContinueSetup) && (
              <Button variant="primary" disabled={checkout.isPending} onClick={() => checkout.mutate()}>
                {canContinueSetup ? 'Continue payment setup' : 'Subscribe monthly'}
              </Button>
            )}
            {data.billing_provider === 'stripe' && !canSubscribe && (
              <Button variant="secondary" disabled={portal.isPending} onClick={() => portal.mutate()}>
                Manage billing
              </Button>
            )}
            {data.billing_provider === 'razorpay' &&
              active &&
              !data.subscription_cancel_at_period_end && (
                <Button
                  variant="secondary"
                  disabled={cancel.isPending}
                  onClick={() =>
                    window.confirm('Cancel the subscription after the current paid month?') &&
                    cancel.mutate()
                  }
                >
                  Cancel at period end
                </Button>
              )}
            {data.billing_provider === 'mock' && active && (
              <Button
                variant="secondary"
                disabled={cancel.isPending}
                onClick={() =>
                  window.confirm('Cancel this development mock subscription?') && cancel.mutate()
                }
              >
                Cancel mock subscription
              </Button>
            )}
          </div>
          <p className="tc-ai-help">
            This month's allowance: {data.monthly_text_quota} rephrasings and{' '}
            {data.monthly_image_quota} pictures. Ask TeachCraftAI if your school needs more.
          </p>
        </>
      ) : (
        <ByokForm schoolId={schoolId} data={data} onApplied={onApplied} />
      )}
    </Section>
  )
}

/** The school's own provider key.
 *
 *  The key field is always blank on load and blank means "keep the one you
 *  have": a saved key is never sent back to the browser, and a form that
 *  pretended otherwise would invite someone to "confirm" a value they cannot
 *  actually see. */
function ByokForm({
  schoolId,
  data,
  onApplied,
}: {
  schoolId: string
  data: AiSchoolSettings
  onApplied: (updated: AiSchoolSettings) => void
}) {
  const queryClient = useQueryClient()
  const [textProvider, setTextProvider] = useState(data.byok_text_provider ?? 'openai')
  const [imageProvider, setImageProvider] = useState(data.byok_image_provider ?? 'openai')
  const [apiKey, setApiKey] = useState('')
  const [baseUrl, setBaseUrl] = useState(data.byok_api_base_url ?? '')
  const [textModel, setTextModel] = useState(data.byok_text_model ?? '')
  const [imageModel, setImageModel] = useState(data.byok_image_model ?? '')
  const [test, setTest] = useState<string | null>(null)

  const onError = (error: unknown) =>
    toast(error instanceof Error ? error.message : 'That could not be saved.', 'error')

  const save = useMutation({
    mutationFn: () =>
      api.setAiByok(schoolId, {
        text_provider: textProvider || null,
        image_provider: imageProvider || null,
        api_key: apiKey || null,
        base_url: baseUrl || null,
        text_model: textModel || null,
        image_model: imageModel || null,
      }),
    onSuccess: (updated) => {
      onApplied(updated)
      setApiKey('')
      queryClient.invalidateQueries({ queryKey: ['ai', 'status'] })
      toast('AI provider settings saved.')
    },
    onError,
  })
  const clear = useMutation({
    mutationFn: () => api.clearAiByok(schoolId),
    onSuccess: (updated) => {
      onApplied(updated)
      setApiKey('')
      queryClient.invalidateQueries({ queryKey: ['ai', 'status'] })
      toast('Key removed.')
    },
    onError,
  })
  const runTest = useMutation({
    mutationFn: (feature: 'grammar' | 'image') => api.testAiConnection(schoolId, feature),
    onSuccess: (result) =>
      setTest(result.ok ? `Connected in ${result.latency_ms ?? 0} ms.` : result.message),
    onError: (error) => setTest(error instanceof Error ? error.message : 'The test failed.'),
  })

  return (
    <>
      <p>
        <Pill tone={data.byok_key_configured ? 'success' : 'muted'}>
          {data.byok_key_configured ? 'Key saved' : 'No key saved yet'}
        </Pill>
      </p>
      <div className="tc-two-up">
        <Field label="Rephrasing provider">
          <Select value={textProvider} onChange={(event) => setTextProvider(event.target.value)}>
            <option value="">Not used</option>
            <option value="openai">OpenAI</option>
          </Select>
        </Field>
        <Field label="Picture provider">
          <Select value={imageProvider} onChange={(event) => setImageProvider(event.target.value)}>
            <option value="">Not used</option>
            <option value="openai">OpenAI</option>
            <option value="gemini">Gemini</option>
          </Select>
        </Field>
      </div>
      <Field
        label="API key"
        hint={
          data.byok_key_configured
            ? 'Leave blank to keep the key already saved.'
            : 'Your provider issues this. It is encrypted before it is stored and never shown again.'
        }
      >
        <Input
          type="password"
          autoComplete="off"
          value={apiKey}
          placeholder={data.byok_key_configured ? '••••••••' : ''}
          onChange={(event) => setApiKey(event.target.value)}
        />
      </Field>
      <Field label="Endpoint" hint="Blank for the provider's public API. Set this for Azure OpenAI or a gateway.">
        <Input value={baseUrl} onChange={(event) => setBaseUrl(event.target.value)} />
      </Field>
      <div className="tc-two-up">
        <Field label="Text model" hint={`Blank uses ${data.default_text_model}.`}>
          <Input value={textModel} onChange={(event) => setTextModel(event.target.value)} />
        </Field>
        <Field label="Picture model" hint={`Blank uses ${data.default_image_model}.`}>
          <Input value={imageModel} onChange={(event) => setImageModel(event.target.value)} />
        </Field>
      </div>
      <div className="tc-ai-form-actions">
        <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>
          Save provider settings
        </Button>
        <Button
          variant="secondary"
          disabled={runTest.isPending || !data.byok_key_configured}
          onClick={() => runTest.mutate('grammar')}
        >
          Test rephrasing
        </Button>
        <Button
          variant="secondary"
          disabled={runTest.isPending || !data.byok_key_configured}
          onClick={() => runTest.mutate('image')}
        >
          Test pictures
        </Button>
        {data.byok_key_configured && (
          <Button
            variant="danger"
            disabled={clear.isPending}
            onClick={() => window.confirm('Remove the saved key?') && clear.mutate()}
          >
            Remove key
          </Button>
        )}
      </div>
      {test && <p role="status">{test}</p>}
    </>
  )
}

function Usage({ schoolId }: { schoolId: string }) {
  const usage = useQuery({
    queryKey: ['ai-usage', schoolId],
    queryFn: () => api.aiUsage(schoolId),
  })
  if (!usage.data) return null
  const data = usage.data
  return (
    <Section title="AI Usage" summary="Teacher AI activity at your school over the last 30 days, updated as usage is recorded.">
      <div className="tc-ai-usage-grid">
        <UsageMetric label="Rephrasing suggestions" value={data.grammar_calls} note={`${data.grammar_accepted} accepted`} />
        <UsageMetric label="Rephrasing acceptance" value={formatRate(data.grammar_acceptance_rate)} note="Accepted by teachers" />
        <UsageMetric label="Pictures generated" value={data.image_calls} note={`${data.image_accepted} kept`} />
        <UsageMetric label="Picture keep rate" value={formatRate(data.image_acceptance_rate)} note="Saved by teachers" />
      </div>
    </Section>
  )
}

function UsageMetric({ label, value, note }: { label: string; value: number | string; note: string }) {
  return (
    <div className="tc-ai-usage-metric">
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{note}</small>
    </div>
  )
}

function formatRate(value: number | null | undefined) {
  return value == null ? '—' : `${Math.round(value * 100)}%`
}
